import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
    Users, 
    ShieldCheck, 
    UserPlus,
    Mail,
    Key,
    Edit,
    Trash2,
    X,
    XCircle,
    CheckCircle2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away.css';
import GlassCard from '../components/UI/GlassCard';
import Table from '../components/UI/Table';

const Usuarios = () => {
    const { can } = useAuth();
    const [usuarios, setUsuarios] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // Formulario
    const [formData, setFormData] = useState({
        id: null,
        nombre: '',
        apellido: '',
        email: '',
        rol_id: 2, // Default: Recepcionista
        activo: true,
        password: ''
    });

    const fetchUsuarios = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('/backend/api/usuarios/index.php');
            if (response.data.success) {
                setUsuarios(response.data.data);
            }
        } catch (error) {
            toast.error('Error al sincronizar lista de personal');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRoles = useCallback(async () => {
        try {
            const response = await axios.get('/backend/api/roles/index.php');
            if (response.data.success) {
                setRoles(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar roles');
        }
    }, []);

    useEffect(() => {
        fetchUsuarios();
        fetchRoles();
    }, [fetchUsuarios, fetchRoles]);

    // Inicializar Tippy al terminar de cargar o filtrar (React 19 safe)
    useEffect(() => {
        if (!loading) {
            tippy('[data-tippy-content]', {
                animation: 'shift-away',
                theme: 'translucent',
                arrow: true
            });
        }
    }, [usuarios, loading]);

    const handleOpenModal = (usuario = null) => {
        if (usuario) {
            setIsEditing(true);
            setFormData({
                id: usuario.id,
                nombre: usuario.nombre,
                apellido: usuario.apellido,
                email: usuario.email,
                rol_id: usuario.rol_id,
                activo: usuario.activo == 1,
                password: '' // No se carga la contraseña actual por seguridad
            });
        } else {
            setIsEditing(false);
            setFormData({
                id: null,
                nombre: '',
                apellido: '',
                email: '',
                rol_id: 2,
                activo: true,
                password: ''
            });
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/backend/api/usuarios/save.php', formData);
            if (response.data.success) {
                toast.success(response.data.message);
                setShowModal(false);
                fetchUsuarios();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Falla en el motor de guardado');
        }
    };

    const handleDelete = async (id, nombre) => {
        const result = await Swal.fire({
            title: '¿Eliminar Usuario?',
            text: `¿Estás seguro de que deseas eliminar permanentemente a ${nombre}? Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#1e293b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            background: '#0f172a',
            color: '#fff',
            backdrop: 'rgba(0,0,0,0.8)'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.post('/backend/api/usuarios/delete.php', { id });
                if (response.data.success) {
                    toast.success(response.data.message);
                    fetchUsuarios();
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Error al eliminar');
            }
        }
    };



    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen">
            {/* Header Módulo */}
            <header className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <div className="inline-flex items-center gap-2 glass px-4 py-1.5 rounded-full border-white/5 mb-4">
                        <ShieldCheck size={14} className="text-primary-light" />
                        <span className="text-[10px] font-black uppercase tracking-[2px] text-white/50">Gobernanza de Identidad</span>
                    </div>
                    <h1 className="text-5xl font-black text-white tracking-tighter">
                        Gestión de <span className="text-primary-light italic font-medium">Usuarios</span>
                    </h1>
                </div>
                {can('Usuarios', 'crear') && (
                    <button 
                        onClick={() => handleOpenModal()}
                        className="group bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-2xl flex items-center gap-3 font-bold transition-all shadow-xl shadow-primary/20 active:scale-95"
                    >
                        <UserPlus size={20} className="group-hover:animate-bounce" />
                        Nuevo Integrante
                    </button>
                )}
            </header>



            {/* Listado Principal Glassmorphic (Standardized with Table Component) */}
            <GlassCard className="!p-0 border-white/5 overflow-hidden min-h-[500px]">
                <Table 
                    headers={['Personal', 'Credencial', 'Nivel de Acceso', 'Estado']}
                    data={usuarios.map(user => ({
                        _raw: user,
                        personal: (
                            <div className="flex items-center gap-4 py-2">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-primary to-primary-light flex items-center justify-center font-black text-xl text-white shadow-lg group-hover:scale-110 transition-transform">
                                    {user.nombre[0]}
                                </div>
                                <div>
                                    <p className="text-white font-bold text-lg leading-none">{user.nombre} {user.apellido}</p>
                                    <p className="text-[10px] text-white/30 uppercase font-black tracking-widest mt-1">ID Central: #{user.id.toString().padStart(4, '0')}</p>
                                </div>
                            </div>
                        ),
                        credencial: (
                            <div className="flex flex-col">
                                <div className="flex items-center gap-2 text-white/60 font-medium">
                                    <Mail size={14} className="text-primary-light" />
                                    {user.email}
                                </div>
                                <span className="text-[10px] text-white/20 mt-1 uppercase tracking-tighter">JWT Auth Ready</span>
                            </div>
                        ),
                        nivel: (
                            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-[10px] font-black uppercase tracking-widest
                                ${user.rol_id == 1 ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400 shadow-lg shadow-indigo-500/5' : 
                                  user.rol_id == 2 ? 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400' : 
                                  'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'}
                            `}>
                                <ShieldCheck size={12} />
                                {user.rol_nombre}
                            </span>
                        ),
                        estado: (user.activo == 1 ? (
                            <div className="inline-flex items-center gap-2 text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-500/20">
                                <div className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse"></div>
                                Activo
                            </div>
                        ) : (
                            <div className="inline-flex items-center gap-2 text-red-400 bg-red-500/10 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-red-500/20">
                                <XCircle size={10} />
                                Baja
                            </div>
                        ))
                    }))}
                    actions={(row) => (
                        <div className="flex items-center justify-end gap-3">
                            {can('Usuarios', 'editar') && (
                                <button 
                                    onClick={() => handleOpenModal(row._raw)}
                                    className="p-3 rounded-xl glass hover:bg-primary/20 hover:text-primary-light transition-all active:scale-95"
                                >
                                    <Edit size={18} />
                                </button>
                            )}
                            {can('Usuarios', 'eliminar') && (
                                <button 
                                    onClick={() => handleDelete(row._raw.id, row._raw.nombre)}
                                    className="p-3 rounded-xl glass hover:bg-red-500/20 hover:text-red-400 transition-all active:scale-95"
                                >
                                    <Trash2 size={18} />
                                </button>
                            )}
                        </div>
                    )}
                    emptyMessage="No se encontraron integrantes del equipo"
                />
            </GlassCard>

            {/* Modal Glassmorphic de Gestión */}
            {showModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 animate-in fade-in duration-300">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setShowModal(false)}></div>
                    <div className="relative w-full max-w-xl glass-heavy rounded-[40px] border border-white/10 shadow-2xl overflow-hidden animate-in zoom-in duration-500">
                        <div className="p-10">
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h2 className="text-3xl font-black text-white italic tracking-tight">
                                        {isEditing ? 'Actualizar Identidad' : 'Nueva Identidad'}
                                    </h2>
                                    <p className="text-white/40 text-xs mt-1 font-bold tracking-widest uppercase">Completa las credenciales de acceso</p>
                                </div>
                                <button 
                                    onClick={() => setShowModal(false)}
                                    className="p-3 rounded-2xl bg-white/5 text-white/40 hover:text-white transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSave} className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-white/30 ml-4">Nombre</label>
                                        <input 
                                            type="text" 
                                            required
                                            value={formData.nombre}
                                            onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                                            className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 px-6 text-white outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white/10 transition-all font-bold"
                                            placeholder="Ejem: Juan"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-white/30 ml-4">Apellido</label>
                                        <input 
                                            type="text" 
                                            required
                                            value={formData.apellido}
                                            onChange={(e) => setFormData({...formData, apellido: e.target.value})}
                                            className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 px-6 text-white outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white/10 transition-all font-bold"
                                            placeholder="Ejem: Perez"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-white/30 ml-4">Correo Electrónico (Acceso)</label>
                                    <div className="relative group">
                                        <Mail className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-primary-light" size={18} />
                                        <input 
                                            type="email" 
                                            required
                                            value={formData.email}
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 pl-16 pr-6 text-white outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white/10 transition-all font-bold"
                                            placeholder="usuario@pawspa.com"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-white/30 ml-4">
                                        {isEditing ? 'Nueva Contraseña (Opcional)' : 'Contraseña de Acceso'}
                                    </label>
                                    <div className="relative group">
                                        <Key className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-primary-light" size={18} />
                                        <input 
                                            type="password" 
                                            required={!isEditing}
                                            value={formData.password}
                                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                                            className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 pl-16 pr-6 text-white outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white/10 transition-all font-bold"
                                            placeholder="••••••••"
                                        />
                                    </div>
                                    {isEditing && <p className="text-[9px] text-white/20 ml-4">Deja en blanco para conservar la actual.</p>}
                                </div>

                                <div className="grid grid-cols-2 gap-6 items-end">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-white/30 ml-4">Nivel de Acceso</label>
                                        <select 
                                            value={formData.rol_id}
                                            onChange={(e) => setFormData({...formData, rol_id: e.target.value})}
                                            className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 px-6 text-white outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white/10 transition-all font-bold appearance-none"
                                        >
                                            {roles.map(rol => (
                                                <option key={rol.id} value={rol.id} className="bg-slate-900">{rol.nombre}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => setFormData({...formData, activo: !formData.activo})}
                                        className={`flex items-center justify-center gap-2 py-4 rounded-2xl border transition-all font-black text-[10px] uppercase tracking-widest
                                            ${formData.activo 
                                                ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400 shadow-lg shadow-emerald-500/5' 
                                                : 'bg-red-500/10 border-red-500/20 text-red-400'}
                                        `}
                                    >
                                        {formData.activo ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                                        {formData.activo ? 'Estado: Activo' : 'Estado: Inactivo'}
                                    </button>
                                </div>

                                <button 
                                    type="submit"
                                    className="w-full bg-primary hover:bg-primary-dark text-white py-5 rounded-2xl font-black uppercase tracking-[3px] text-xs shadow-2xl shadow-primary/30 active:scale-95 transition-all mt-8"
                                >
                                    {isEditing ? 'Confirmar Actualización' : 'Protocolo de Alta de Usuario'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Usuarios;
